import { useState } from 'react';

import { ApiError } from '@/api/client';
import type { CandidateRead } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Combobox } from '@/components/ui/Combobox';
import { Drawer } from '@/components/ui/Drawer';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useUsers } from '@/features/users/api';

import {
  useCandidateDepartments,
  useCountries,
  usePositions,
  useUpdateCandidate,
} from '../api';
import {
  APPLYING_FOR_OPTIONS,
  DOCUMENTATION_OPTIONS,
  PAYMENT_OPTIONS,
  POINTS_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  referenceOptions,
} from '../labels';
import {
  candidateFieldsSchema,
  changeCandidateField,
  fieldsFrom,
  messagesFrom,
  serverMessageFor,
  toUpdateBody,
  type CandidateFieldsDraft,
  type FieldMessages,
} from '../schema';
import styles from './CandidateForm.module.css';

const FORM_ID = 'candidate-edit';

/**
 * Edit a candidate.
 *
 * The screening fields are deliberately absent. `flyst_stage`, `rsr_state` and
 * `rsr_scheduled_on` move through the screening endpoints, which carry rules a plain
 * update would walk straight past: a scheduled date is required to arrange a
 * sponsor screening, and finishing an RSR screening needs an ATS account first. Those
 * endpoints arrive with the boards; putting the fields in this form now would be the
 * bypass the plan explicitly forbids.
 *
 * Uploads are absent because they are a different endpoint. They are made on the
 * create form; replacing one afterwards is not built yet.
 */
export function CandidateDrawer({
  candidate,
  onClose,
}: {
  candidate: CandidateRead;
  onClose: () => void;
}) {
  // Seeded once. The drawer is mounted only while open, so every open starts from the
  // record as it currently is rather than from whatever was typed and abandoned last
  // time.
  const [draft, setDraft] = useState<CandidateFieldsDraft>(() => fieldsFrom(candidate));
  const [messages, setMessages] = useState<FieldMessages>({});

  const update = useUpdateCandidate();
  const departments = useCandidateDepartments();
  const positions = usePositions();
  const countries = useCountries();

  // The staff list, for the assignee. One page is the endpoint's maximum and is well
  // past the real headcount; the combobox filters what it is given.
  const staff = useUsers({
    page: 1,
    per_page: 100,
    search: '',
    status: 'active',
    department_id: '',
    sort_by: 'full_name',
    sort_dir: 'asc',
  });

  const set = <K extends keyof CandidateFieldsDraft>(
    key: K,
    value: CandidateFieldsDraft[K],
  ) => {
    setDraft((current) => changeCandidateField(current, key, value));
    // A message under a field is about what was there when it was submitted, so it
    // goes the moment the field changes.
    setMessages((current) => ({ ...current, [key]: undefined }));
  };

  const error = update.error instanceof ApiError ? update.error : null;

  const messageFor = (field: keyof CandidateFieldsDraft) =>
    messages[field] ?? serverMessageFor(error, field);

  const save = () => {
    const parsed = candidateFieldsSchema.safeParse(draft);
    if (!parsed.success) {
      setMessages(messagesFrom(parsed.error));
      return;
    }

    // Every field, every time. The server applies exactly what it is sent, and all of
    // this came off the record. The schema is what decides which of them may be null.
    update.mutate({ id: candidate.id, ...toUpdateBody(parsed.data) }, { onSuccess: onClose });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Edit record"
      description={`${candidate.full_name} · ${candidate.reference}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      {/* Anything the server said that is not about one field. A validation failure is
          already showing under the inputs, so it would only repeat itself here. */}
      {error && !error.isValidation ? <Alert tone="danger">{error.message}</Alert> : null}

      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <fieldset className={styles.section}>
          <legend className={styles.legend}>Personal details</legend>

          <Field label="Full name" required error={messageFor('full_name')}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={draft.full_name}
                onChange={(event) => {
                  set('full_name', event.target.value);
                }}
              />
            )}
          </Field>

          <div className={styles.pair}>
            <Field label="Email" error={messageFor('email')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="email"
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.email}
                  onChange={(event) => {
                    set('email', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Phone" error={messageFor('phone')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.phone}
                  onChange={(event) => {
                    set('phone', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          <div className={styles.pair}>
            <Field label="City" error={messageFor('city')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.city}
                  onChange={(event) => {
                    set('city', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Country" error={messageFor('country')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.country}
                  onChange={(event) => {
                    set('country', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>
        </fieldset>

        <fieldset className={styles.section}>
          <legend className={styles.legend}>Application</legend>

          <div className={styles.pair}>
            <Field label="Received on" required error={messageFor('received_on')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="date"
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.received_on}
                  onChange={(event) => {
                    set('received_on', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Source">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.source}
                  options={SOURCE_OPTIONS}
                  onChange={(event) => {
                    set('source', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          <div className={styles.pair}>
            <Field label="Applying for">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.applying_for}
                  options={APPLYING_FOR_OPTIONS}
                  onChange={(event) => {
                    set('applying_for', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Department" error={messageFor('department_id')}>
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  placeholder="No department"
                  value={draft.department_id}
                  options={referenceOptions(departments.data, draft.department_id)}
                  onChange={(event) => {
                    set('department_id', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          <div className={styles.pair}>
            <Field label="Preferred position" error={messageFor('preferred_position_id')}>
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  placeholder="No preference"
                  value={draft.preferred_position_id}
                  options={referenceOptions(positions.data, draft.preferred_position_id)}
                  onChange={(event) => {
                    set('preferred_position_id', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Preferred country" error={messageFor('preferred_country_id')}>
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  placeholder="No preference"
                  value={draft.preferred_country_id}
                  options={referenceOptions(countries.data, draft.preferred_country_id)}
                  onChange={(event) => {
                    set('preferred_country_id', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>
        </fieldset>

        <fieldset className={styles.section}>
          <legend className={styles.legend}>Tracking</legend>

          <div className={styles.pair}>
            <Field label="Status">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.status}
                  options={STATUS_OPTIONS}
                  onChange={(event) => {
                    set('status', event.target.value);
                  }}
                />
              )}
            </Field>

            {draft.status === 'rsr_screening_arranged' ? (
              <Field
                label="RSR screening date"
                required
                hint="The candidate will appear on the RSR board for this booking."
              >
                {(ids) => (
                  <Input
                    {...ids}
                    type="date"
                    required
                    value={draft.rsr_booking_date}
                    onChange={(event) => {
                      set('rsr_booking_date', event.target.value);
                    }}
                  />
                )}
              </Field>
            ) : null}

            <Field label="Sponsor points" error={messageFor('sponsor_points')}>
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.sponsor_points}
                  options={POINTS_OPTIONS}
                  onChange={(event) => {
                    set('sponsor_points', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          <div className={styles.pair}>
            <Field label="Documentation">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.documentation}
                  options={DOCUMENTATION_OPTIONS}
                  onChange={(event) => {
                    set('documentation', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Payment">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.payment_status}
                  options={PAYMENT_OPTIONS}
                  onChange={(event) => {
                    set('payment_status', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          <Field label="Assigned to" error={messageFor('assigned_to')}>
            {({ id, describedBy, invalid }) => (
              <Combobox
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                placeholder="Unassigned"
                value={draft.assigned_to}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...(staff.data?.items ?? []).map((user) => ({
                    value: user.id,
                    label: user.full_name,
                    meta: user.email,
                  })),
                ]}
                onChange={(value) => {
                  set('assigned_to', value);
                }}
              />
            )}
          </Field>

          <div className={styles.checks}>
            <Checkbox
              label="Contacted"
              checked={draft.contacted}
              onChange={(event) => {
                set('contacted', event.target.checked);
              }}
            />
            <Checkbox
              label="Details shared"
              checked={draft.details_shared}
              onChange={(event) => {
                set('details_shared', event.target.checked);
              }}
            />
            <Checkbox
              label="Follow-up done"
              checked={draft.followup_done}
              onChange={(event) => {
                set('followup_done', event.target.checked);
              }}
            />
          </div>
        </fieldset>
      </form>
    </Drawer>
  );
}
